import I18n from "components/materials/I18n";
import classes from "./classes.module.scss";
import Button, { EButtonVariant } from "components/elements/Button";
import Typography, { ITypo } from "components/elements/Typography";
import TotpInput from "components/materials/Form/TotpInput";
import { useCallback, useContext, useEffect } from "react";
import RequestPinCodeButton from "./RequestPinCodeButton";
import { LoginFormContext } from "contexts/LoginForm";
import UserCheckPinRequestResource from "common/resources/User/UserCheckPinRequestResource";
import { container } from "tsyringe";
import AuthService from "services/AuthService";
import useHead from "hooks/useHead";
import Form from "components/materials/Form";
import { AuthContext } from "contexts/Auth";
import UserLoginRequestResource from "common/resources/User/UserLoginRequestResource";
import { useNavigate } from "react-router-dom";
import ModuleConfig from "configs/ModuleConfig";

const authService = container.resolve(AuthService);
const moduleConfig = container.resolve(ModuleConfig);

export default function PinCodeLoginStep() {
	const navigate = useNavigate();
	const authContext = useContext(AuthContext);
	const loginFormContext = useContext(LoginFormContext);
	const email = loginFormContext.formData?.email;
	const baseKeyTrad = I18n.asset.pages.login.pin_code_login;

	useHead({
		title: I18n.trslt(baseKeyTrad.page_title),
	});

	const checkPinCode = useCallback(async () => {
		if (!email) throw new Error("Email is not defined");
		const pinCode = document.querySelector<HTMLInputElement>("[name=pinCode]")!.value;

		const userCheckPinRequestResource = UserCheckPinRequestResource.hydrate<UserCheckPinRequestResource>({ email, pinCode });
		const userLoginRequestResource = UserLoginRequestResource.hydrate<UserLoginRequestResource>({
			email,
			password: loginFormContext.loginFormData?.password!,
			pinCode,
		});

		try {
			await userCheckPinRequestResource.validateOrReject();
			await userLoginRequestResource.validateOrReject(); // Already validated in the ConnectStep
			await authService.checkPinCode(userCheckPinRequestResource);
			await authContext.login(userLoginRequestResource);
		} catch (error) {
			console.error(error);
			if (error instanceof Array) {
				loginFormContext.setErrors(error);
			}
		}
	}, [email, loginFormContext, authContext]);

	const onCancel = useCallback(() => {
		loginFormContext.setLoginFormData(null);
	}, [loginFormContext]);

	useEffect(() => {
		if (authContext.jwtContent) {
			navigate(moduleConfig.get().modules.pages.Home.props.path);
		}
	}, [authContext.jwtContent, navigate]);

	if (!email) return null;

	return (
		<Form errors={loginFormContext.errors} onSubmit={checkPinCode} name="user-authentification">
			<div className={classes["root"]}>
				<div className={classes["title-container"]}>
					<Typography typo={ITypo.H1}>
						<I18n text={baseKeyTrad.title} />
					</Typography>
					<Typography typo={ITypo.P_MEDIUM}>
						<I18n text={baseKeyTrad.description} />
					</Typography>
				</div>

				<div className={classes["content"]}>
					<Typography typo={ITypo.P_LARGE_BOLD} className={classes["email"]}>
						{email}
					</Typography>

					<RequestPinCodeButton email={email} autoSend />

					<TotpInput label={I18n.trslt(I18n.asset.pages.login.request_pin_code.label)} name="pinCode" />

					<div className={classes["button-container"]}>
						<Button type="submit">
							<I18n text={I18n.asset.common.continue} />
						</Button>
						<Button variant={EButtonVariant.OUTLINED} onClick={onCancel}>
							<I18n text={I18n.asset.common.cancel} />
						</Button>
					</div>
				</div>
			</div>
		</Form>
	);
}
